import { useState } from "react";
import { Link, Redirect } from "wouter";
import { useConsentHistory } from "@orbit/api-client-react";
import type { ConsentHistoryItem } from "@orbit/api-zod";
import { useAuth } from "../hooks/useAuth";
import { Wordmark } from "../components/Brand";

/**
 * /profile/consents — chronological audit log of every consent the
 * customer has granted/withdrawn AND every account event (profile
 * edits, phone/email changes, account-deletion request/cancel). The
 * server returns a flat, friendly-typed feed; we render it as a simple
 * timeline with "Load more" pagination via the cursor.
 *
 * Cursor is opaque — we just hold the last `nextCursor` and pass it
 * through to the next page. Items accumulate in local state so the
 * user can scroll continuously.
 */
export function ProfileConsentHistoryPage() {
  const { isAuthenticated } = useAuth();
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [items, setItems] = useState<ConsentHistoryItem[]>([]);
  const [exhausted, setExhausted] = useState(false);

  const q = useConsentHistory(
    { cursor, limit: 20 },
    {
      enabled: isAuthenticated && !exhausted,
    },
  );

  // Fold each new page into the running list. We do this inside an
  // effect-free render guard because TanStack guarantees `data` is
  // referentially stable per page key — once the cursor changes, this
  // block runs exactly once per page.
  if (q.data && (items.length === 0 || items[items.length - 1]?.id !== q.data.items[q.data.items.length - 1]?.id)) {
    // Append only if this page isn't already in the buffer.
    const tailId = q.data.items[q.data.items.length - 1]?.id;
    const alreadyHave = tailId && items.some((it) => it.id === tailId);
    if (!alreadyHave) {
      setItems((prev) => [...prev, ...q.data!.items]);
      if (q.data.nextCursor === null) setExhausted(true);
    }
  }

  if (!isAuthenticated) return <Redirect to="/" />;

  return (
    <div className="min-h-screen pb-16">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-xs text-ink-dark underline">
            Back
          </Link>
        </header>
        <h1 className="mt-6 text-xl font-bold text-navy">Consent history</h1>
        <p className="mt-2 text-sm text-ink-dark">
          Every privacy choice you've made on Orbit.
        </p>

        <ul className="mt-6 space-y-2">
          {items.length === 0 && !q.isLoading && (
            <li className="text-sm text-ink-dark">No history yet.</li>
          )}
          {items.map((it) => (
            <ConsentRow key={it.id} item={it} />
          ))}
        </ul>

        {!exhausted && (
          <button
            type="button"
            className="btn-secondary w-full mt-6"
            disabled={q.isLoading || q.isFetching}
            onClick={() => {
              if (q.data?.nextCursor) setCursor(q.data.nextCursor);
            }}
          >
            {q.isLoading || q.isFetching ? "Loading…" : "Load more"}
          </button>
        )}
        {exhausted && items.length > 0 && (
          <p className="mt-6 text-center text-xs text-ink-dark/70">
            That's everything.
          </p>
        )}
      </div>
    </div>
  );
}

const TYPE_LABELS: Record<string, string> = {
  TERMS_OF_SERVICE: "Agreed to Terms of Service",
  PRIVACY_POLICY: "Agreed to Privacy Policy",
  MARKETING_SMS: "Marketing — text messages",
  MARKETING_EMAIL: "Marketing — email",
  DATA_SHARING: "Data sharing with merchant",
  PROFILE_UPDATED: "Profile updated",
  PHONE_CHANGED: "Phone number changed",
  EMAIL_CHANGED: "Email changed",
  ACCOUNT_DELETION_REQUESTED: "Account deletion requested",
  ACCOUNT_DELETION_CANCELLED: "Account deletion cancelled",
  MERCHANT_REVOKED: "Revoked merchant access",
  MERCHANT_REENROLLED: "Re-joined merchant",
};

function ConsentRow({ item }: { item: ConsentHistoryItem }) {
  const label = TYPE_LABELS[item.type] ?? item.type.replace(/_/g, " ");
  const ts = new Date(item.grantedAt).toLocaleString();
  const isBoolean = item.type.startsWith("MARKETING_") || item.type === "DATA_SHARING";
  return (
    <li className="card py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-navy">{label}</p>
          {item.tenantName && (
            <p className="text-[11px] uppercase tracking-wider text-ink-dark/70">
              {item.tenantName}
            </p>
          )}
          <p className="text-xs text-ink-dark/80">{ts}</p>
        </div>
        {isBoolean && (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
              item.granted
                ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-700"
            }`}
          >
            {item.granted ? "On" : "Off"}
          </span>
        )}
      </div>
    </li>
  );
}
